import React, { ReactNode } from "react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import KenColors from "./kenColors";

interface GlobalThemeProviderProps {
  children: ReactNode;
}

const theme = createTheme({
  palette: {
    KenColors: { ...KenColors },
    primary: {
      light: "#F0EDF6",
      main: "#604797",
      dark: "#604797",
      contrastText: "#fff",
    },
    secondary: {
      light: "#ff7961",
      main: "#f44336",
      dark: "#ba000d",
      contrastText: "#000",
    },
    error: {
      main: "#E75547",
    },
    success: {
      main: "#1BBE75",
    },
    card: {
      backgroundColor: "#F7F8FB",
      hover: "#EDF2FF",
    },
    labels: {
      main: "#061938",
      light: "#7A869A",
      medium: "#505F79",
    },
  },
  typography: {
    fontSize: 14,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontFamily: ["Schibsted Grotesk"].join(","),
    body1: {
      fontSize: "16",
    },
    body2: {
      fontSize: 18,
      fontWeight: 500,
    },
    caption: {
      fontSize: "14px",
    },
    h1: {
      fontSize: "20px",
      fontWeight: 500,
      "@media (min-width:1710)": {
        fontSize: "15px",
      },
    },
    h2: {
      fontSize: "40px",
    },
    h3: {
      fontSize: "32px",
    },
    h4: {
      fontSize: "24px",
    },
    h5: {
      fontSize: "16px",
    },
    h6: {
      fontSize: "14px",
      color: "#fff",
    },
    subtitle1: {
      fontSize: "14px",
      fontWeight: 400,
      color: "#FEFEFE",
    },
    subtitle2: {
      fontSize: "14px",
      fontWeight: 400,
      color: "#fff",
    },
  },
  components: {
    MuiTextField: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
          width: "100%",
          marginBottom: "16px",
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          color: "#B5B3B3", // Text color
          backgroundColor: "#101010",
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: "#E35C16",
        },
        focused: {
          color: "#E35C16",
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#fff3",
            // boxShadow: "0px 4px 30px 0px rgba(227, 92, 22, 0.30)",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#fff3",
            // boxShadow: "0px 4px 30px 0px rgba(227, 92, 22, 0.30)",
          },
        },
        notchedOutline: {
          borderColor: "#fff3",
          // boxShadow: "0px 4px 30px 0px rgba(227, 92, 22, 0.30)",
          borderRadius: "8px",
          borderWidth: ".5px",
        },
      },
    },

    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: "#1A1A1A", // dropdown background
          color: "#ffffff", // option text color
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: "14px",
          fontWeight: 400,
          padding: "16px",
          borderBottom: "1px solid #373A45", // divider between items
          "&:last-of-type": {
            borderBottom: "none", // remove divider after last item
          },
          "&.Mui-selected": {
            backgroundColor: "#2A2A2A", // selected option background
            "&:hover": {
              backgroundColor: "#333333", // selected + hover
            },
          },
          "&:hover": {
            backgroundColor: "#292929", // hover background
          },
        },
      },
    },

    MuiRadio: {
      styleOverrides: {
        root: {
          color: "#E35C16",
          "&.Mui-checked": {
            color: "#E35C16",
          },
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          color: "#fff",
        },
      },
    },
    MuiButton: {
      variants: [
        {
          props: { variant: "contained" },
          style: {
            textTransform: "capitalize",
            background: "#fff",
            color: "#fff",
            ":hover": {
              background: "#fff",
            },
          },
        },
        {
          props: { variant: "text" },
          style: {
            textTransform: "capitalize",
          },
        },
        {
          props: { variant: "outlined" },
          style: {
            textTransform: "capitalize",
            border: "1px solid #fff",
            color: "#fff",
            ":hover": {
              border: "1px solid #fff",
            },
          },
        },
      ],
    },
  },
  breakpoints: {
    values: {
      xs: 0, // Extra small devices (phones)
      sm: 576, // Small devices (landscape phones)
      md: 768, // Medium devices (tablets)
      lg: 992, // Large devices (desktops)
      xl: 1200, // Extra large devices (large desktops)
      xxl: 1440, // Extra wide desktops (e.g., 1440px width monitors)
      HD: 1500,
      fullHD: 1710, // Full HD (1920px width) or larger screens like wide desktop monitors
      ultraHD: 1920, // 4k or larger monitors
    },
  },
});

const GlobalThemeProvider: React.FC<GlobalThemeProviderProps> = ({
  children,
}) => {
  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};

export default GlobalThemeProvider;
